import { isoZ, z } from 'my-zod'
import { match } from 'ts-pattern'
import { enumOptionsToObject } from '../../internal'

const geographyOptions = ['INTL', 'NA', 'CAN', '49ST', '49CAN', 'CAL', '50ST', 'CND'] as const

const productLineOptions = [
  'ATV',
  'RGR',
  'TSL',
  'OSP',
  'VIC',
  'RZR',
  'IND',
  'LEV',
  'YTH',
  'SLG',
  'MIL',
  'GEN',
  'COM',
  'GPL',
  'TLD',
  'SNO',
  'PWC',
  'BRU',
  'XVR',
  'BOA'
] as const
const productLines = enumOptionsToObject(productLineOptions)

function getGeographiesForLocation(location: 'international' | 'california' | 'fortyNineState' | 'canada' | 'mexico') {
  return match<typeof location, Model2Schemas.Geography[]>(location)
    .with('california', () => ['NA', 'CAL', '50ST'])
    .with('international', () => ['INTL'])
    .with('fortyNineState', () => ['NA', '49ST', '49CAN', '50ST'])
    .with('canada', () => ['NA', 'CAN', 'CND'])
    .with('mexico', () => ['INTL'])
    .exhaustive()
}

const modelAttributeSourceOptions = [
  'snowflake',
  'wholegoodsApi',
  'polarisAdventuresUserDefined',
  'polarisAdventuresSystemDefined',
  'erpApi'
] as const
const modelAttributeSources = enumOptionsToObject(modelAttributeSourceOptions)

const modelAttribute = z.object({
  source: z.enum(modelAttributeSourceOptions),
  category: z.string(),
  name: z.string(),
  value: z.unknown(),
  description: z.string()
})

const model2Model = z.object({
  modelNumber: z.string().min(1).max(20),
  sequenceNumber: z.number().int().nullable(),
  modelDescription: z.string().max(120),
  productType: z.string().max(1).nullable(),
  featureSequence: z.number().int().nullable(),
  marketingName: z.string().max(60).nullable(),
  modelYear: z.number().min(1900),
  productGroup: z.string().max(20).nullable(),
  productSegment: z.string().max(45).nullable(),
  productSubsegment: z.string().max(45).nullable(),
  productSupersegment: z.string().max(45).nullable(),
  manufacturingSubsegment: z.string().max(45).nullable(),
  crossRefProductType: z.string().max(1).nullable(),
  modelType: z.string().max(3).nullable(),
  baseModelNumber: z.string().max(20).nullable(),
  productLine: z.enum(productLineOptions),
  subProductLine: z.string().max(40).nullable(),
  reportProductLine: z.string().max(3).nullable(),
  msrp: z.number().nullable(),
  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant()
})

const modelWithAttributes = model2Model.augment({ attributes: modelAttribute.array() })

const model2UserDefinedAttributes = z.object({
  isAdventuresOwnedEligible: z.boolean(),
  isFleetSaleEligible: z.boolean(),
  notes: z.string()
})

const model2SystemDefinedAttributes = z.object({
  mainImageUrl: z.string()
})

const modelAttributeInfo = z.object({
  geography: z.enum(geographyOptions).nullable(),
  color: z.string().nullable(),
  isPolarisAdventuresLeaseEligible: z.boolean(),
  isPolarisAdventuresFleetSaleEligible: z.boolean(),
  polarisAdventuresNotes: z.string(),
  mainImageUrl: z.string().nullable(),
  personCapacity: z.number().nullable()
})

const modelAttributeToModelAttributeInfoMapper = {
  geography: {
    default: null,
    attributes: [
      {
        source: 'snowflake',
        name: 'geography',
        category: ''
      }
    ]
  },
  color: {
    default: null,
    attributes: [
      {
        source: 'wholegoodsApi',
        name: 'color',
        category: ''
      },
      {
        source: 'snowflake',
        name: 'color',
        category: ''
      }
    ]
  },
  isPolarisAdventuresLeaseEligible: {
    default: false,
    attributes: [
      {
        source: 'polarisAdventuresUserDefined',
        name: 'isAdventuresOwnedEligible',
        category: ''
      }
    ]
  },
  isPolarisAdventuresFleetSaleEligible: {
    default: false,
    attributes: [
      {
        source: 'polarisAdventuresUserDefined',
        name: 'isFleetSaleEligible',
        category: ''
      }
    ]
  },
  polarisAdventuresNotes: {
    default: '',
    attributes: [
      {
        source: 'polarisAdventuresUserDefined',
        name: 'notes',
        category: ''
      }
    ]
  },
  mainImageUrl: {
    default: null,
    attributes: [
      {
        source: 'polarisAdventuresSystemDefined',
        name: 'mainImageUrl',
        category: ''
      }
    ]
  },
  personCapacity: {
    default: null,
    attributes: [{ source: 'wholegoodsApi', name: 'Person Capacity', category: 'dimensions' }],
    transform: (attributes) => (attributes.length && attributes[0].value ? Number(attributes[0].value) : null)
  }
} satisfies {
  [key in keyof z.infer<typeof modelAttributeInfo>]: AttributeMapper<z.infer<typeof modelAttributeInfo>[key]>
}

interface AttributeMapper<T> {
  default: T
  attributes: {
    source: (typeof modelAttributeSourceOptions)[number]
    name: string
    category: string
  }[]
  transform?: (attributes: Model2Schemas.modelAttribute[]) => T
}

const modelAttributesForAttributeInfo = Object.values(modelAttributeToModelAttributeInfoMapper)
  .map((a) => a.attributes)
  .flat()

function getModelAttributeInfo({ attributes }: { attributes: z.infer<typeof modelAttribute>[] }) {
  const attributeInfo = Object.fromEntries(
    Object.entries(modelAttributeToModelAttributeInfoMapper).map(([key, value]) => {
      const mapper = value as AttributeMapper<unknown>
      const matchingAttributes = mapper.attributes.flatMap((a) => {
        const match = attributes.find((m) => a.source === m.source && m.name === a.name)
        return match ? [match] : []
      })

      const result = mapper.transform
        ? mapper.transform(matchingAttributes)
        : matchingAttributes.length
        ? (matchingAttributes[0].value as unknown)
        : null
      const parsed = modelAttributeInfo.shape[key as keyof typeof modelAttributeInfo.shape].safeParse(result)
      if (parsed.success) {
        return [key, parsed.data]
      } else {
        return [key, value.default]
      }
    })
  )
  return modelAttributeInfo.parse(attributeInfo)
}

const modelWithAttributeInfo = model2Model.merge(modelAttributeInfo)

const modelDocumentSchema = z.object({
  id: z.string(),
  modelNumber: z.string().min(1),
  type: z.string(),
  title: z.string(),
  revisionNumber: z.string().nullable(),
  sourceUri: z.string(),
  extension: z.string(),
  cultureCode: z.string(),
  mediaNumber: z.string(),
  bulletinNumber: z.string().nullable(),
  createdDate: isoZ.instant()
})

export const Model2Schemas = {
  model2Model,
  modelWithAttributes,
  modelWithAttributeInfo,
  getGeographiesForLocation,
  model2UserDefinedAttributes,
  model2SystemDefinedAttributes,
  productLineOptions,
  productLines,
  modelAttribute,
  modelAttributesForAttributeInfo,
  modelAttributeSourceOptions,
  modelAttributeSources,
  modelDocumentSchema,

  modelAttributeInfo,
  getModelAttributeInfo
}

export declare namespace Model2Schemas {
  export type ProductLine = (typeof productLineOptions)[number]
  export type model2Model = z.infer<typeof model2Model>
  export type modelWithAttributes = z.infer<typeof modelWithAttributes>
  export type modelWithAttributeInfo = z.infer<typeof modelWithAttributeInfo>
  export type model2UserDefinedAttributes = z.infer<typeof model2UserDefinedAttributes>
  export type model2SystemDefinedAttributes = z.infer<typeof model2SystemDefinedAttributes>
  export type modelAttributeInfo = z.infer<typeof modelAttributeInfo>
  export type modelAttribute = z.infer<typeof modelAttribute>
  export type modelDocumentSchema = z.infer<typeof modelDocumentSchema>

  export type Geography = (typeof geographyOptions)[number]
  export type AttributeSource = (typeof modelAttributeSourceOptions)[number]
}
